import _ from 'lodash';
import { Map as ImmutableMap } from 'immutable';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsSelectors } from 'app-components/flux/chart-of-accounts';
import { dateSelectors } from 'app-components/flux/date';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import {
  getRootModalRemoveDialogOptions,
  ROOT_MODAL_DRAWER_PROP,
  withDrawerWidth,
  type StandardDrawerWidth,
} from 'components/Drawers/standardDrawerContract';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { getInvoices } from 'app-components/flux/transactions/invoicesSelectors';
import {
  applyDefaultBusinessIdToSplitItems,
  isAnySplitTransferCounterpart,
  omitLinkedItemsFromSplit,
  splitItemsToArray,
  type ReceiptSplitSeed,
} from 'app-components/flux/transactions/splitsHelpers';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
// Import after transactions: receiptsSelectors depends on transactionsSelectors, and loading
// receipts first triggers the transactions → investment-holdings → accounts module cycle.
import { receiptsActions, receiptsSelectors } from 'app-components/flux/receipts';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import { getLogger, tracker } from 'app-components/utils/core';
import { TransactionRuleAction } from 'components/Dialogs/TransactionRules/actions';
import { showViewSeriesDialog } from 'components/Dialogs/ViewSeriesDialog/actions';
import { showSplitDialog } from 'components/Transactions/SplitDialog/actions';
import { repairReceiptAttachmentSources } from 'components/Transactions/ReceiptSection/receiptTransaction';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { isSupportedTransferTxn } from 'data/transactions/utils';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';
import { useUrlDialog } from 'hooks/useUrlDialog';
import { useDispatch } from 'react-redux';

import type { TransactionEditViewModel } from '../components/TransactionEditViewModel';
import { isArAssociatedPaymentTxn } from '../selectors';
import { showRecordInvoicePaymentDialog } from '../showRecordInvoicePaymentDialog';
import type { TransactionEditDialogProps, ResourceTxnAction, TransactionDetailsMPProps } from '../types';
import { createWorkflowState, workflowActions, workflowReducer, getTitle, getWorkflowView } from '../workflowReducer';
import { transactionDetailsFormHooks } from './formHooks';
import { useBusinessContextSelection } from './useBusinessContextSelection';
import { useCurrentTransactionDraft } from './useCurrentTransactionDraft';
import { useRecurringTransactionWorkflow } from './useRecurringTransactionWorkflow';
import { useStaleTransactionRefresh } from './useStaleTransactionRefresh';
import { useTransactionDetailsDisplayState } from './useTransactionDetailsDisplayState';
import { useTransactionDetailsFormFields } from './useTransactionDetailsFormFields';
import { useTransactionEditGuards } from './useTransactionEditGuards';
import { useVisibilityDisplayState } from './useVisibilityDisplayState';

const log = getLogger('component/Dialogs/TransactionEditDialog/hooks/useTransactionEditViewModel.ts');

const RULE_DIALOG_ID = 'add-memorized-rule';

type Params = TransactionEditDialogProps & {
  saveFnRef: MutableRefObject<((txn: CashFlowTransaction) => void) | null>;
};

type CloseAllOptions = {
  animate?: boolean;
};

type Result = {
  closeAll: (isSuccess?: boolean) => void;
  otherTransfer: CashFlowTransaction | null;
  viewModel: TransactionEditViewModel;
};

function getTransactionDetailsMPProps(props: TransactionDetailsMPProps) {
  const { action, context, txn: mpTxn, ...otherProps } = props;
  const category = chartOfAccountsSelectors.getCoaStringSelector(null, mpTxn?.coa, true);
  const type = mpTxn.isBill
    ? 'BILL'
    : mpTxn.isSubscription
      ? 'SUBSCRIPTION'
      : scheduledTransactionsUtils.typeFromCOA(mpTxn?.coa ?? { id: '', type: 'NONE' });

  return { action, source: 'txnDetails', context, payee: mpTxn?.payee, category, type, ...otherProps };
}

export function useTransactionEditViewModel({
  onClose,
  recurring,
  defaultAccountId,
  focusSplits,
  wrapperId,
  isPrefilled,
  txnActionCallback,
  txn: txnProp,
  disableSalesReceiptFields,
  receiptId,
  rootModalDrawer: rootModalDrawerProp,
  saveFnRef,
  [ROOT_MODAL_DRAWER_PROP]: rootModalDrawerOption,
}: Params): Result {
  const rootModalDrawer = rootModalDrawerProp ?? rootModalDrawerOption;
  const pendingForceResetFromQcsRef = useRef(false);
  const pendingMarkRecurringAfterSaveRef = useRef(false);
  const preserveDrawerAfterRuleSaveRef = useRef(false);
  const closeAllRef = useRef<(isSuccess?: boolean, options?: CloseAllOptions) => void>(() => {});
  const store = useAppStore();
  const dispatch = useDispatch();
  const { closeDialog } = useUrlDialog();

  const manager = transactionDetailsFormHooks.useManager();

  const form = useTransactionDetailsFormFields(isPrefilled);
  const { setCoa, setIsBill } = form.setters;

  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const openAccounts = useAppSelector((state) =>
    accountsSelectors.getOpenOrSelectedCashAccounts(state, form.derived.txn?.accountId || ''),
  );
  const allAccounts = useAppSelector(accountsSelectors.getResourcesById);
  const isTxnFromArAccount = useAppSelector(
    (state) => accountsSelectors.getAccountById(state, form.derived.txn?.accountId)?.type === 'ACCOUNTS_RECEIVABLE',
  );
  const futureTxnsFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'futureTxns'));
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  // Gates the redesigned "skinny" drawer detail view. When off, the drawer keeps the previous
  // (roomy, modal-style) layout across the board.
  const enhancedDetailView = useAppSelector((state) =>
    Boolean(featureFlagsSelectors.getFeatureFlag(state, 'transactionsEnhancedDetailViewRelease')),
  );
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);
  const [_excludeIncomeByDefault] = usePreferenceV2('sSPExcludeIncomeByDefault');

  const connectedTxn = useAppSelector((state) =>
    form.derived.txn?.id ? transactionsSelectors.getTransactionById(state, form.derived.txn.id) : undefined,
  );
  // The receipts service owns the receipt/transaction link, so resolve it by transaction id rather
  // than trusting the RECEIPT-sourced attachment — transaction writes can strip the attachment's
  // source, and the link must survive that.
  const hasReceiptAttachment = Boolean(form.derived.txn?.attachments?.some(transactionsTypes.isReceiptAttachment));
  const linkedReceipt = useAppSelector((state) =>
    receiptId
      ? receiptsSelectors.selectReceiptById(state, receiptId)
      : form.derived.txn?.id
        ? receiptsSelectors.selectReceiptByTransactionId(state, form.derived.txn.id)
        : undefined,
  );
  const txnReceiptId = receiptId ?? linkedReceipt?.id;
  const shouldSyncReceiptData = useAppSelector(receiptsSelectors.selectShouldSyncReceiptData);
  const receiptsSynced = useAppSelector((state) => receiptsSelectors.selectLastSyncDate(state) !== null);
  const hasAnyAttachment = Boolean(form.derived.txn?.attachments?.length);
  const appDispatch = useAppDispatch();
  useEffect(() => {
    // Receipts are normally loaded by the Receipt Center route; fetch them here when a
    // receipt-linked transaction is opened before that route has been visited. Attachments can
    // lose their RECEIPT source on transaction writes, so before the first receipts sync any
    // attachment-bearing transaction is a fetch candidate.
    if (shouldSyncReceiptData && !txnReceiptId && (hasReceiptAttachment || (hasAnyAttachment && !receiptsSynced))) {
      appDispatch(receiptsActions.fetchReceipts(undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSyncReceiptData, hasReceiptAttachment, hasAnyAttachment, appDispatch]);
  const transactionsById = useAppSelector(transactionsSelectors.getResourcesById);
  const scheduledTransactionsById = useAppSelector(scheduledTransactionsSelectors.getResourcesById);
  const arAccountsByBusinessId = useAppSelector(accountsSelectors.getArAccountByBusiness);
  const arAccountIds = useAppSelector(accountsSelectors.getAllArAccountIds);

  const currentDate = useAppSelector(dateSelectors.getCurrentDate);

  const create = !transactionsUtils.isExistingTxn(form.derived.txn);
  const isManual = form.derived.txn.source === 'USER_ENTERED';

  const [workflow, workflowDispatch] = useReducer(workflowReducer, recurring, createWorkflowState);
  const {
    open,
    screen,
    showUnsavedSplitsChanges,
    showUnsavedRecurringPrompt,
    txnClientId,
    waitingForTxn,
    waitingForTransfer,
    completionText,
    modelIdToLink,
    otherTransfer,
  } = workflow;
  const showRecurringOptions =
    screen.type === 'recurring-options' || screen.type === 'recurring-select' || screen.type === 'recurring-create';
  const editRecurring = screen.type === 'recurring-create';
  const editSeries = screen.type === 'edit-series';
  const editSeriesInitialTab = screen.type === 'edit-series' ? screen.initialTab : 0;
  const editSeriesAssignSplits = screen.type === 'edit-series' ? screen.assignSplits : false;
  const showConfirmation = screen.type === 'review';
  const [dateError, setDateError] = useState<string | undefined>();

  const dateInfoText = useMemo(() => {
    if (!futureTxnsFf) return null;
    if (form.derived.date > currentDate) return 'Future dates will create a Reminder.';
    return null;
  }, [currentDate, form.derived.date, futureTxnsFf]);

  const isAssetPayment = useMemo(
    () => isArAssociatedPaymentTxn({ coa: form.derived.coa, split: form.derived.split }, arAccountIds),
    [arAccountIds, form.derived.coa, form.derived.split],
  );

  const linkedInvoiceIndex = useAppSelector(transactionsSelectors.getLinkedInvoiceIndexIfLoaded);

  // Restore the RECEIPT source on the linked receipt's attachment when a write stripped it, so it
  // never renders as an empty document chip and stays typed on the next save.
  const attachmentsWithReceiptSources = useMemo(
    () => repairReceiptAttachmentSources(form.values.attachments, linkedReceipt),
    [form.values.attachments, linkedReceipt],
  );

  const currentTxn = useCurrentTransactionDraft({
    accountId: form.values.accountId,
    amount: form.values.amount,
    attachments: attachmentsWithReceiptSources,
    businessId: form.values.businessId,
    checkNumber: form.values.checkNumber,
    coaRaw: form.values.coaRaw,
    connectedTxn,
    customersAndProjects: form.values.customersAndProjects,
    excludeF2S: form.values.excludeF2S,
    excludeReports: form.values.excludeReports,
    isBill: form.values.isBill,
    isAssetPayment,
    isReviewed: form.values.isReviewed,
    linkedInvoiceIndex,
    memo: form.values.memo,
    originalTxnJS: form.values.originalTxnJS,
    payee: form.values.payee,
    postedOn: form.values.postedOn,
    scheduledTransactionsById,
    splitRaw: form.values.splitRaw,
    status: form.derived.status,
    tagsArr: form.values.tagsArr,
    transactionsById,
    userFlag: form.values.userFlag,
  });

  const tempTxn = connectedTxn || form.derived.txn;
  const hasSplitTransferCounterpart = useMemo(
    () =>
      isAnySplitTransferCounterpart({
        txn: tempTxn,
        transactionsById,
        scheduledModelTxnsById: scheduledTransactionsById,
      }),
    [scheduledTransactionsById, tempTxn, transactionsById],
  );

  const incomeCOAs = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const isIncome = transactionsUtils.isIncomeFromTxn(currentTxn, incomeCOAs);
  const isCredit = useAppSelector((state) =>
    chartOfAccountsSelectors.isCreditBill(state, form.values.accountId, form.derived.coa),
  );
  const isTransfer = transactionsUtils.isUITransferTxn(currentTxn) && !isCredit;
  const refundTracker = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'refundTracker'));

  const model = useAppSelector((state) =>
    form.derived.txn.stModelId
      ? scheduledTransactionsSelectors.getScheduledTransactionById(state, form.derived.txn.stModelId)
      : undefined,
  );
  const seriesTxn = model?.transaction ?? null;

  const invoicesLength = useAppSelector((state) => getInvoices(state).length);

  const disabledPayment = useCallback(
    (bId?: string | null) =>
      Boolean(
        (bId && (!arAccountsByBusinessId?.has(bId) || invoicesLength === 0)) ||
          !(
            form.values.accountId && accountsUtils.doesAccountSupportPaymentSrcs(allAccounts.get(form.values.accountId))
          ),
      ),
    [arAccountsByBusinessId, invoicesLength, form.values.accountId, allAccounts],
  );

  const showCheckNumberField = useAppSelector((state) =>
    accountsSelectors.shouldAccountDisplayCheckNumberField(state, form.values.accountId),
  );

  const {
    currentSplitPayments,
    deleteQTip,
    editSplitsFromExcludeBannerDisabled,
    fullDisableQBP,
    linkedExpense,
    showPaymentDialog,
    showPaymentDialogLink,
    splitDialogReadOnly,
    splitsDisabled,
  } = useTransactionEditGuards({
    arAccountIds,
    businessEnabled,
    coa: form.derived.coa,
    create,
    currentTxn,
    disableSalesReceiptFields,
    isAssetPayment,
    tempTxn,
    txn: form.derived.txn,
  });

  const resetFormStateFromTxn = useCallback(
    (nextTxn: CashFlowTransaction) => {
      manager.reinit({
        txn: nextTxn,
        defaultAccountId,
        txnWasProvided: true,
      });
    },
    [manager, defaultAccountId],
  );

  const { ornamentType, resetCustomersAndProjects, selectAccount, setCustomersAndProjects } =
    useBusinessContextSelection({
      accountsById,
      amount: form.values.amount,
      businessEnabled,
      businessId: form.values.businessId,
      categoriesById,
      coa: form.derived.coa,
      create,
      currentTxn,
      isAssetPayment,
      setAccountId: form.setters.setAccountIdInternal,
      setBusinessId: form.setters.setBusinessId,
      setCoa,
      setCustomersAndProjectsInternal: form.setters.setCustomersAndProjectsInternal,
    });

  const exitUnsavedDialog = useCallback(() => {
    workflowDispatch(workflowActions.exitUnsavedDialog());
  }, []);

  const showUnsavedDialog = useCallback(() => {
    workflowDispatch(workflowActions.showUnsavedSplitsDialog());
  }, []);

  const showUnsavedRecurringDialog = useCallback(() => {
    workflowDispatch(workflowActions.showUnsavedRecurringDialog());
  }, []);

  const exitRecurringCreate = useCallback(() => {
    workflowDispatch(workflowActions.exitEditRecurring());
    if (editRecurring && model) {
      dispatch(scheduledTransactionsActions.deleteScheduledTransaction(model.set('isUserVerified', true)));
      manager.fireAction(
        'updateBaseTxn',
        transactionsTypes.mkCashFlowTransaction(form.derived.txn.merge({ stModelId: undefined, isBill: false })),
      );
      setIsBill(false);
    }
  }, [dispatch, editRecurring, manager, model, setIsBill, form.derived.txn]);

  const resetToEdit = useCallback(() => {
    exitRecurringCreate();
    workflowDispatch(workflowActions.resetToEdit());
  }, [exitRecurringCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeAll = useCallback(
    (isSuccess?: boolean, options?: CloseAllOptions) => {
      log.log('\nclosingAll');
      workflowDispatch(workflowActions.closeDialog());
      resetToEdit();
      onClose?.(isSuccess);
      closeDialog(`TRANSACTION_DETAILS_${txnProp?.id || '0'}`);
      dispatch(
        rootUiActions.removeDialog(`TRANSACTION_DETAILS_${txnProp?.id || '0'}`, {
          animate: options?.animate ?? true,
        }),
      );
    },
    [closeDialog, dispatch, onClose, resetToEdit, txnProp?.id],
  );
  closeAllRef.current = closeAll;

  const { performTransactionAction, saveTransactions, deleteTransactions, unMatchTransaction } = useTxnActions({
    wrapperId: wrapperId || 'TRANSACTION_DETAILS_DIALOG',
    robotMode: showPaymentDialog || showPaymentDialogLink,
    callback: (ret: ResourceTxnAction[]) => {
      txnActionCallback?.(ret);
      if (ret) {
        if (pendingMarkRecurringAfterSaveRef.current) {
          pendingMarkRecurringAfterSaveRef.current = false;
          workflowDispatch(workflowActions.showRecurringOptions());
        }
        const txnId = currentTxn?.id ?? ret.find((res) => res.clientId === txnClientId)?.id;
        if (showPaymentDialog) {
          showRecordInvoicePaymentDialog({ closeAll, currentTxn, dispatch, txnId });
        }
      }
    },
  });

  saveFnRef.current = (txnToSave: CashFlowTransaction) => {
    const shouldPreserveDrawerAfterSave = preserveDrawerAfterRuleSaveRef.current;
    preserveDrawerAfterRuleSaveRef.current = false;

    saveTransactions([create ? txnToSave.set('clientId', txnClientId) : txnToSave], {
      txnOverrides: {
        isExcludedFromF2S: txnToSave.isExcludedFromF2S,
        isExcludedFromReports: txnToSave.isExcludedFromReports,
      },
    });
    if (!showPaymentDialog && !shouldPreserveDrawerAfterSave) closeAll(true);
  };

  const unlinkRecurring = useCallback(() => {
    tracker.track(
      tracker.events.txnUpdatedInFTSRegister,
      getTransactionDetailsMPProps({ action: 'unlinkRecurring', context: 'details-dialog', txn: form.derived.txn }),
    );

    if (form.derived.txn.stModelId) {
      if (splitSeriesFf) {
        pendingForceResetFromQcsRef.current = true;
      }
      performTransactionAction(
        { action: 'unlink-to-scheduled-txn', id: form.derived.txn.id },
        // @ts-expect-error not ts
        { undo: { userMessage: 'Transaction unlinked.' } },
      );
      manager.fireAction(
        'updateBaseTxn',
        transactionsTypes.mkCashFlowTransaction(form.derived.txn.set('stModelId', undefined)),
      );
      setIsBill(false);
    }
  }, [manager, performTransactionAction, setIsBill, splitSeriesFf, form.derived.txn]);

  const unMarkAsBill = useCallback(() => setIsBill(false), [setIsBill]);

  const onDelete = useCallback(() => {
    deleteTransactions([form.derived.txn]);
    closeAll();
  }, [closeAll, deleteTransactions, form.derived.txn]);

  const onSave = useCallback(() => {
    manager.submit(undefined);
  }, [manager]);

  const onCreateRule = useCallback(() => {
    const isDrawerLaunch = Boolean(rootModalDrawer);
    if (form.derived.isDirty) {
      preserveDrawerAfterRuleSaveRef.current = isDrawerLaunch;
      onSave();
    } else if (!isDrawerLaunch) {
      closeAll(undefined, { animate: false });
    }

    dispatch(
      TransactionRuleAction({
        id: RULE_DIALOG_ID,
        isDrawerOverlay: isDrawerLaunch,
        transaction: currentTxn,
        handleClose: () => {
          dispatch(rootUiActions.removeDialog(RULE_DIALOG_ID));
          if (!isDrawerLaunch) {
            onClose?.();
          }
        },
        originalIsReviewed: form.derived.txn.isReviewed,
      }),
    );

    tracker.track(tracker.events.txnRuleStarted, { source: 'txnDetails' });
  }, [
    closeAll,
    currentTxn,
    dispatch,
    form.derived.isDirty,
    form.derived.txn.isReviewed,
    onClose,
    onSave,
    rootModalDrawer,
  ]);

  const supportedTransfer = useMemo(
    () => form.derived.txn.coa && form.derived.txn.coa.type === 'ACCOUNT' && isSupportedTransferTxn(form.derived.txn),
    [form.derived.txn],
  );
  const isMatched = useMemo(
    () =>
      transactionsUtils.getMatchState(form.derived.txn) === 'matched' &&
      form.derived.txn.source !== 'SCHEDULED_TRANSACTION',
    [form.derived.txn],
  );
  const showMatchOrTransferRow = isMatched || supportedTransfer;

  const goToOtherSide = useCallback((foundTxn: CashFlowTransaction) => {
    workflowDispatch(workflowActions.startWaitingForTransfer());
    setTimeout(() => {
      workflowDispatch(workflowActions.showOtherTransfer(foundTxn));
    }, 500);
  }, []);

  const showSplitDialogForTransaction = useCallback(
    (transaction: CashFlowTransaction) => {
      if (hasSplitTransferCounterpart) return;

      const dlgId = `SPLIT_DIALOG_${transaction.id || txnClientId || '0'}`;
      const closeSplitDialog = () =>
        dispatch(rootUiActions.removeDialog(dlgId, getRootModalRemoveDialogOptions(rootModalDrawer)));
      resetCustomersAndProjects();
      dispatch(
        showSplitDialog({
          id: dlgId,
          txn: transaction,
          setSplit: form.setters.setSplit,
          setAmount: form.setters.setAmount,
          setCoa,
          setTags: form.setters.setTags,
          accountId: form.values.accountId,
          disabledPaymentFn: disabledPayment,
          setCustomersAndProjects,
          readOnly: splitDialogReadOnly,
          rootModalDrawer,
          onBack: closeSplitDialog,
          onClose: closeSplitDialog,
        }),
      );
    },
    [
      form.values.accountId,
      disabledPayment,
      dispatch,
      hasSplitTransferCounterpart,
      resetCustomersAndProjects,
      rootModalDrawer,
      splitDialogReadOnly,
      form.setters.setAmount,
      setCustomersAndProjects,
      form.setters.setSplit,
      form.setters.setTags,
      txnClientId,
      setCoa,
    ],
  );

  const openSplitDialog = useCallback(() => {
    showSplitDialogForTransaction(currentTxn);
  }, [currentTxn, showSplitDialogForTransaction]);

  const openReceiptSplits = useCallback(
    (seed: ReceiptSplitSeed): void => {
      const split = {
        ...seed.split,
        items: applyDefaultBusinessIdToSplitItems(seed.split.items, currentTxn.businessId),
      };
      showSplitDialogForTransaction(
        currentTxn.merge({
          amount: seed.amount,
          coa: null,
          split,
        }) as CashFlowTransaction,
      );
    },
    [currentTxn, showSplitDialogForTransaction],
  );

  const hasSplitItemsDefined = splitItemsToArray(form.derived.split?.items ?? []).length > 0;
  const { disableReportsValue, visibilityExcludeReportsDisplay, visibilityF2SDisplay } = useVisibilityDisplayState({
    coa: form.derived.coa,
    excludeF2S: form.values.excludeF2S,
    excludeReports: form.values.excludeReports,
    hasSplitItemsDefined,
    split: form.derived.split,
    transactionsById,
  });

  const openSplitDialogFromExclusionsBanner = useCallback(() => {
    openSplitDialog();
    tracker.track(tracker.events.viewSplitsV2, {
      initiator: form.derived.txn.id === null ? 'createTxn' : 'editTxnWithSplits',
      transactionAmount: form.values.amount,
      transactionAmountSign: form.values.amount >= 0 ? 'positive' : 'negative',
    });
  }, [form.values.amount, openSplitDialog, form.derived.txn.id]);

  const discardAndMarkRecurring = useCallback(() => {
    exitUnsavedDialog();
    resetFormStateFromTxn(connectedTxn || form.derived.txn);
    workflowDispatch(workflowActions.showRecurringOptions());
  }, [connectedTxn, exitUnsavedDialog, resetFormStateFromTxn, form.derived.txn]);

  const updateAndMarkRecurring = useCallback(() => {
    exitUnsavedDialog();
    pendingMarkRecurringAfterSaveRef.current = true;
    saveTransactions([create ? currentTxn.set('clientId', txnClientId) : currentTxn], {
      txnOverrides: {
        isExcludedFromF2S: currentTxn.isExcludedFromF2S,
        isExcludedFromReports: currentTxn.isExcludedFromReports,
      },
    });
  }, [create, currentTxn, exitUnsavedDialog, saveTransactions, txnClientId]);

  const {
    closeRecurring,
    createRecurring,
    dismissReview,
    doOneTimeBill,
    exitRecurringSelect,
    linkRecurring,
    markRecurring,
    onCloseReview,
    saveRecurring,
    startRecurringSelect,
    stopRecurring,
    updateRecurring,
  } = useRecurringTransactionWorkflow({
    businessId: form.values.businessId,
    closeAll,
    create,
    currentTxn,
    dispatch,
    excludeF2S: form.values.excludeF2S,
    excludeReports: form.values.excludeReports,
    getTransactionDetailsMPProps,
    isDirty: form.derived.isDirty,
    manager,
    modelIdToLink,
    performTransactionAction,
    recurring,
    resetToEdit,
    saveTransactions,
    setIsBill,
    showUnsavedRecurringDialog,
    txn: form.derived.txn,
    workflowDispatch,
  });

  useEffect(() => {
    if (connectedTxn) {
      log.info('new QCS txn', connectedTxn?.toJS?.());
      const matchedTxnId = connectedTxn.matchedTxn?.id;
      if (connectedTxn.source === 'SCHEDULED_TRANSACTION_PENDING' && matchedTxnId && connectedTxn.id !== matchedTxnId) {
        log.info('reminder restored, switching to txn with ID:', matchedTxnId);
        const bankTxn = transactionsSelectors.getTransactionById(store.getState(), matchedTxnId);
        if (bankTxn && pendingForceResetFromQcsRef.current) {
          resetFormStateFromTxn(bankTxn);
          pendingForceResetFromQcsRef.current = false;
        } else {
          manager.fireAction(
            'updateBaseTxn',
            bankTxn ?? transactionsTypes.mkCashFlowTransaction(connectedTxn.set('id', matchedTxnId)),
          );
        }
      } else {
        log.info('updating to new QCS txn');
        if (pendingForceResetFromQcsRef.current) {
          resetFormStateFromTxn(connectedTxn);
          pendingForceResetFromQcsRef.current = false;
        } else {
          manager.fireAction('updateBaseTxn', connectedTxn);
          if (connectedTxn.stModelId) {
            form.setters.setIsBillInternal(connectedTxn.isBill ?? false);
          }
        }
      }
    } else if (!create && waitingForTxn) {
      log.log('Txn was auto-deleted after link to recurring!\nLooking for auto-matched reminder');
      const state = store?.getState();
      const autoMatchedTxn =
        state && currentTxn.id && form.derived.txn?.accountId
          ? transactionsSelectors.getTxnByMatchedTxnAndAccountId(state, currentTxn.id, form.derived.txn.accountId)
          : undefined;
      if (autoMatchedTxn) {
        log.log('Found reminder!\nSwitching to matched txn');
        workflowDispatch(workflowActions.stopWaitingForTxn());
        manager.fireAction('updateBaseTxn', autoMatchedTxn);
        form.setters.setIsBillInternal(autoMatchedTxn.isBill ?? false);
      } else {
        log.warn(
          'Looked for auto-matched reminder after txn was deleted, but could not find it!\n' +
            'Closing dialog to prevent editing a deleted txn',
        );
        closeAllRef.current();
      }
    }
  }, [connectedTxn]); // eslint-disable-line react-hooks/exhaustive-deps

  const { isDirtyOutside, onRefreshClick } = useStaleTransactionRefresh({
    closeAll,
    connectedTxn,
    defaultAccountId,
    dispatch,
    focusSplits,
    onClose,
    refreshTransaction: resetFormStateFromTxn,
    recurring,
    txnActionCallback,
    wrapperId,
  });

  const { completeEnough, hasSplitSeries, hideClientsAndProjects, hideCreateRule, isAccountClosed, validPayee } =
    useTransactionDetailsDisplayState({
      accountId: form.values.accountId,
      attachments: form.values.attachments,
      customersAndProjects: form.values.customersAndProjects,
      dateError,
      dateIsValid: Boolean(form.derived.date?.isValid),
      disableSalesReceiptFields,
      isSplitSeriesEnabled: Boolean(splitSeriesFf),
      openAccounts,
      ornamentType,
      payee: form.values.payee,
      seriesTxn,
      split: form.derived.split,
      txn: form.derived.txn,
    });
  const {
    dialogOpen,
    loadingLine,
    showEditRecurring,
    showRecurringSelect,
    view: workflowView,
    waiting,
  } = getWorkflowView(workflow, {
    model,
    create,
  });

  const paymentId = form.derived.txn.cpData?.cpPaymentIntentId;

  const title = getTitle(workflow, { create, isIncome, isTransfer });
  // Compare split *user content* only — ignore the server-owned `linkedItem`, which drifts as the
  // server bills/unbills the expense and must not be mistaken for an unsaved edit (it otherwise pops
  // a phantom "update?" prompt on close when the user changed nothing).
  const splitHasUnsavedChanges =
    !_.isEqual(omitLinkedItemsFromSplit(form.derived.split), omitLinkedItemsFromSplit(form.derived.txn.split)) ||
    form.derived.isDirty;
  const isRootModalDrawer = Boolean(rootModalDrawer);
  const rootModalDrawerDirtyConfirmation = rootModalDrawer?.dirtyConfirmation;
  const rootModalDrawerShowScrim = rootModalDrawer?.showScrim;
  const rootModalDrawerCloseTransition = rootModalDrawer?.closeTransition;
  // Approach (b): the drawer opens at the previous ('Large') width, then we rebroadcast the
  // flag-driven width at render time. Flag on -> narrow 'Small' skinny drawer; flag off -> the
  // previous 'Large' drawer (no change, no resize). Scoped to the base details drawer; sub-flows
  // still force 'Large' via withDrawerWidth.
  const effectiveDrawerWidth: StandardDrawerWidth = enhancedDetailView ? 'Small' : 'Large';
  // Launched from the narrow ('Small') details drawer, but the view-series drawer
  // needs the roomier width so the details width change stays scoped to details.
  const viewSeriesRootModalDrawer = useMemo(() => withDrawerWidth(rootModalDrawer, 'Large'), [rootModalDrawer]);

  useEffect(() => {
    if (!isRootModalDrawer) {
      return;
    }

    dispatch(
      rootUiActions.updateDialogProps({
        id: `TRANSACTION_DETAILS_${txnProp?.id || '0'}`,
        props: ImmutableMap({
          [ROOT_MODAL_DRAWER_PROP]: {
            closeTransition: rootModalDrawerCloseTransition,
            dirtyConfirmation: rootModalDrawerDirtyConfirmation,
            isDirty: splitHasUnsavedChanges,
            showScrim: rootModalDrawerShowScrim,
            width: effectiveDrawerWidth,
          },
        }),
      }),
    );
  }, [
    dispatch,
    effectiveDrawerWidth,
    isRootModalDrawer,
    rootModalDrawerCloseTransition,
    rootModalDrawerDirtyConfirmation,
    rootModalDrawerShowScrim,
    splitHasUnsavedChanges,
    txnProp?.id,
  ]);

  const onEditSeriesFromReview = useCallback(
    (initialTab = 0) => workflowDispatch(workflowActions.startEditSeries(initialTab)),
    [],
  );
  const onSetEditSeries = useCallback(() => workflowDispatch(workflowActions.startEditSeries()), []);
  const onViewRecurring = useCallback(
    () => dispatch(showViewSeriesDialog({ rootModalDrawer: viewSeriesRootModalDrawer, txn: form.derived.txn })),
    [dispatch, form.derived.txn, viewSeriesRootModalDrawer],
  );

  const viewModel = useMemo<TransactionEditViewModel>(
    () => ({
      actions: {
        closeAll,
        closeRecurring,
        createRecurring,
        dismissReview,
        discardAndMarkRecurring,
        disabledPayment,
        doOneTimeBill,
        exitRecurringCreate,
        exitRecurringSelect,
        exitUnsavedDialog,
        goToOtherSide,
        linkRecurring,
        markRecurring,
        onCloseReview,
        onCreateRule,
        onDelete,
        onEditSeriesFromReview,
        onRefreshClick,
        onSave,
        onSetEditSeries,
        onViewRecurring,
        openSplitDialog,
        openReceiptSplits,
        openSplitDialogFromExclusionsBanner,
        resetCustomersAndProjects,
        saveRecurring,
        selectAccount,
        showUnsavedDialog,
        startRecurringSelect,
        stopRecurring,
        unMarkAsBill,
        unMatchTransaction,
        unlinkRecurring,
        updateAndMarkRecurring,
        updateRecurring,
      },
      derived: {
        allAccounts,
        businessEnabled,
        coa: form.derived.coa,
        completeEnough,
        completionText,
        currentSplitPayments,
        currentTxn,
        date: form.derived.date,
        dateError,
        dateInfoText,
        deleteQTip,
        dialogOpen,
        disableReportsValue,
        disableSalesReceiptFields,
        editSeries,
        editSeriesAssignSplits,
        editSeriesInitialTab,
        editSplitsFromExcludeBannerDisabled,
        enhancedDetailView,
        focusSplits,
        fullDisableQBP,
        futureTxnsFf: Boolean(futureTxnsFf),
        hasSplitItemsDefined,
        hasSplitSeries,
        hasSplitTransferCounterpart,
        hideClientsAndProjects,
        hideCreateRule,
        isAccountClosed: Boolean(isAccountClosed),
        isAssetPayment,
        isDirty: form.derived.isDirty,
        isDirtyOutside,
        isIncome,
        isManual,
        isMatched,
        isTransfer,
        isTxnFromArAccount,
        linkedExpense,
        loadingLine,
        model,
        open,
        openAccounts,
        ornamentType,
        paymentId,
        refundTracker: Boolean(refundTracker),
        receiptId: txnReceiptId,
        rootModalDrawer: rootModalDrawer ?? null,
        showCheckNumberField,
        showConfirmation,
        showEditRecurring,
        showMatchOrTransferRow: Boolean(showMatchOrTransferRow),
        showPaymentDialogLink,
        showRecurringOptions,
        showRecurringSelect,
        showUnsavedRecurringPrompt,
        showUnsavedSplitsChanges,
        split: form.derived.split,
        splitHasUnsavedChanges,
        splitSeriesFf: Boolean(splitSeriesFf),
        splitsDisabled,
        supportedTransfer,
        title,
        txn: form.derived.txn,
        validPayee,
        visibilityExcludeReportsDisplay,
        visibilityF2SDisplay,
        waiting,
        waitingForTransfer,
        workflowView,
      },
      setters: {
        setAttachments: form.setters.setAttachments,
        setBusinessId: form.setters.setBusinessId,
        setCheckNumber: form.setters.setCheckNumberInternal,
        setCoa,
        setCustomersAndProjects,
        setDate: form.setters.setDate,
        setDateError,
        setExcludeF2S: form.setters.setExcludeF2S,
        setExcludeReports: form.setters.setExcludeReports,
        setFlag: form.setters.setFlag,
        setMemo: form.setters.setMemo,
        setPayee: form.setters.setPayee,
        setStatus: form.setters.setStatus,
        setTags: form.setters.setTags,
        updateAmount: form.setters.updateAmount,
        updateReviewed: form.setters.updateReviewed,
      },
      values: {
        accountId: form.values.accountId,
        amount: form.values.amount,
        attachments: attachmentsWithReceiptSources,
        businessId: form.values.businessId,
        checkNumber: form.values.checkNumber,
        create,
        customersAndProjects: form.values.customersAndProjects,
        isBill: form.values.isBill,
        isReviewed: form.values.isReviewed,
        memo: form.values.memo,
        payee: form.values.payee,
        status: form.derived.status,
        userFlag: form.values.userFlag,
      },
    }),
    [
      allAccounts,
      businessEnabled,
      closeAll,
      closeRecurring,
      completeEnough,
      completionText,
      create,
      createRecurring,
      currentSplitPayments,
      currentTxn,
      dismissReview,
      dateError,
      dateInfoText,
      deleteQTip,
      dialogOpen,
      disableReportsValue,
      disableSalesReceiptFields,
      disabledPayment,
      discardAndMarkRecurring,
      doOneTimeBill,
      editSeries,
      editSeriesAssignSplits,
      editSeriesInitialTab,
      editSplitsFromExcludeBannerDisabled,
      enhancedDetailView,
      exitRecurringCreate,
      exitRecurringSelect,
      exitUnsavedDialog,
      focusSplits,
      form.derived.coa,
      form.derived.date,
      form.derived.isDirty,
      form.derived.split,
      form.derived.status,
      form.derived.txn,
      form.setters.setAttachments,
      form.setters.setBusinessId,
      form.setters.setCheckNumberInternal,
      form.setters.setDate,
      form.setters.setExcludeF2S,
      form.setters.setExcludeReports,
      form.setters.setFlag,
      form.setters.setMemo,
      form.setters.setPayee,
      form.setters.setStatus,
      form.setters.setTags,
      form.setters.updateAmount,
      form.setters.updateReviewed,
      form.values.accountId,
      form.values.amount,
      attachmentsWithReceiptSources,
      form.values.businessId,
      form.values.checkNumber,
      form.values.customersAndProjects,
      form.values.isBill,
      form.values.isReviewed,
      form.values.memo,
      form.values.payee,
      form.values.userFlag,
      fullDisableQBP,
      futureTxnsFf,
      goToOtherSide,
      hasSplitItemsDefined,
      hasSplitSeries,
      hasSplitTransferCounterpart,
      hideClientsAndProjects,
      hideCreateRule,
      isAccountClosed,
      isAssetPayment,
      isDirtyOutside,
      isIncome,
      isManual,
      isMatched,
      isTransfer,
      isTxnFromArAccount,
      linkRecurring,
      linkedExpense,
      loadingLine,
      markRecurring,
      model,
      onCloseReview,
      onCreateRule,
      onDelete,
      onEditSeriesFromReview,
      onRefreshClick,
      onSave,
      onSetEditSeries,
      onViewRecurring,
      open,
      openAccounts,
      openSplitDialog,
      openReceiptSplits,
      openSplitDialogFromExclusionsBanner,
      ornamentType,
      paymentId,
      refundTracker,
      txnReceiptId,
      rootModalDrawer,
      resetCustomersAndProjects,
      saveRecurring,
      selectAccount,
      setCoa,
      setCustomersAndProjects,
      setDateError,
      showCheckNumberField,
      showConfirmation,
      showEditRecurring,
      showMatchOrTransferRow,
      showPaymentDialogLink,
      showRecurringOptions,
      showRecurringSelect,
      showUnsavedDialog,
      showUnsavedRecurringPrompt,
      showUnsavedSplitsChanges,
      splitHasUnsavedChanges,
      splitSeriesFf,
      splitsDisabled,
      startRecurringSelect,
      stopRecurring,
      supportedTransfer,
      title,
      unMarkAsBill,
      unMatchTransaction,
      unlinkRecurring,
      updateAndMarkRecurring,
      updateRecurring,
      validPayee,
      visibilityExcludeReportsDisplay,
      visibilityF2SDisplay,
      waiting,
      waitingForTransfer,
      workflowView,
    ],
  );

  return { closeAll, otherTransfer, viewModel };
}
